import type React from 'react'

import { ProjectionKind } from 'generated/resources/datatable.graphql'

import type { BodyCell } from 'modules/core/components/DataTable/types'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

import useData from './useData'
import useProjections from './useProjections'

function getId<T>(args: T, kind: 'cell' | 'row' | 'selectCell', identifier?: string) {
  const id = String((args as { [id: string]: number | string }).id)
  return `${kind}-${identifier ? identifier + '-' : ''}${id}`
}

const useBody = <T, S extends string>(selectable: boolean | undefined) => {
  const { queryResult, source, rowTo, data } = useData<T, S>()
  const { projections } = useProjections<T, S>()

  const { resolver } = source
  const { error } = queryResult

  const body = useUnsafeMemo(() => {
    if (error || !data) return []

    return data.map((args, index) => {
      const argsId = String((args as { [id: string]: number | string }).id)

      const cells: BodyCell[] = projections.map((projection) => {
        const projectionArgs = {
          id: getId(args, 'cell', projection.identifier),
          width: projection.width,
          colSpan: projection.colSpan,
          justifyContent: projection.justifyContent,
          textAlign: projection.textAlign,
          sticky: 'sticky' in projection ? projection.sticky : false,
        }

        if ('render' in projection) {
          return { ...projectionArgs, content: projection.render(args, index) }
        }

        if (projection.kind === ProjectionKind.Path) {
          if (!resolver.fields[projection.path]) {
            throw new Error(
              `Path ${String(
                projection.path
              )} not found in resolver. Please check your projections.`
            )
          }

          const content = args[projection.path] as React.ReactNode
          return { ...projectionArgs, content }
        }

        // eslint-disable-next-line no-console
        console.error('Invalid projection', projection)
        throw new Error('Invalid projection')
      })

      return {
        id: argsId,
        to: rowTo ? rowTo(args) : undefined,
        cells,
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, projections, resolver, resolver.fields, selectable])

  return body
}

export default useBody
